export const addHttpsProtocolToUrl = (url: string) =>
  url.replace(/^(http(s)?:\/\/)?/i, () => "https://");

// Note: noembed might not find a matching provider for some valid youtube links,
// even though patterns like "https?://.*\\.youtube\\.com/v/.*" are provided in the list of
// supported sites at https://noembed.com/providers.

type Provider = "noembed" | "youtube";

const fetchEmbed = async (provider: Provider, canonicalUrl: string) => {
  const providers: Record<Provider, string> = {
    noembed: "https://noembed.com/embed",
    youtube: "https://www.youtube.com/oembed",
  };
  const url = new URL(providers[provider]);
  url.searchParams.set("url", canonicalUrl);
  const response = await fetch(url);
  return await response.json();
};

const youtubeShortLinkBase = "https://youtu.be";

export const getVideoEmbed = async (videoUrl: string) => {
  // if youtube url, extract id into canonical url
  const canonicalUrl = getCanonicalUrl(videoUrl);
  let embed = await fetchEmbed("noembed", canonicalUrl);

  if ("error" in embed) {
    if (
      canonicalUrl.startsWith(youtubeShortLinkBase) &&
      embed.error.includes(403)
    ) {
      embed = await fetchEmbed("youtube", canonicalUrl);
    }
  }

  return embed;
};

export const validVideoUrl = async (value?: string) => {
  let embed = await getVideoEmbed(value);

  if ("error" in embed) {
    return false;
  }

  return ["Vimeo", "YouTube"].includes(embed.provider_name);
};

const youtubeCanonicalUrl = (id: string) => `${youtubeShortLinkBase}/${id}`;

const youtubeIdMatchers = [
  /youtube\.com\/embed\/([\w-]+)/i,
  /youtube\.com\/shorts\/([\w-]+)/i,
  /youtube\.com\/v\/([\w-]+)/i,
  /youtube\.com\/watch\?v=([\w-]+)/i,
  /youtu\.be\/([\w-]+)/i,
];

const youtubeRE =
  /^(((?:https?:)?\/\/)?((?:www|m)\.)?((?:(youtube\.com)\/(((((watch\?v=|embed\/|v\/|shorts\/)[\w-]+(&|\?)?)?)?))|((?:youtu.be))\/([\w-]+)\??))(?:(\w+=\w+)&?)*)$/;

/**
 * Takes a variant of a youtube video url and returns a canonical url for that video, if applicable.
 * If not a valid youtube url, or no id can be extracted, it returns the original string.
 */
export function getCanonicalUrl(url?: string): string {
  const urlWithHttps = addHttpsProtocolToUrl(url ?? "");
  // not a valid youtube url, pass along for additional checks
  if (!urlWithHttps?.match(youtubeRE)) {
    return urlWithHttps;
  }

  for (const matcher of youtubeIdMatchers) {
    const match = urlWithHttps.match(matcher);
    if (match?.[1]) {
      return youtubeCanonicalUrl(match[1]);
    }
  }

  return urlWithHttps;
}

export const hasValidServiceDomain = (
  serviceUrl: string,
  service: { domains: string[] },
) => {
  if (!serviceUrl) {
    return false;
  }

  let url: URL = undefined;
  try {
    url = new URL(addHttpsProtocolToUrl(serviceUrl));
    // ignore invalid urls as the value is either a partial url or already run through a url validator
  } catch (err) {}
  let validDomain = false;
  for (const domain of service.domains) {
    if (url?.hostname.endsWith(domain)) {
      validDomain = true;
    }
  }
  if (!validDomain) {
    return false;
  }

  return true;
};
